import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Table, Button, Badge, Form, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector, useDispatch } from "react-redux";
import { API_WEB_URLS } from "constants/constAPI";
import { Fn_GetReport } from "store/Functions";
import { Download, RefreshCw, AlertCircle, Clock, Truck, MessageCircle } from "react-feather";
import * as XLSX from "xlsx";

function PendingLiftingTracker() {
  const dispatch = useDispatch();
  const globalDates = useSelector(state => state.GlobalDates);

  const [fromDate, setFromDate] = useState(globalDates.fromDate ? new Date(globalDates.fromDate) : new Date());
  const [toDate, setToDate] = useState(globalDates.toDate ? new Date(globalDates.toDate) : new Date());
  const [filterType, setFilterType] = useState("all"); // 'all' | 'buy' | 'sell'
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [state, setState] = useState({ FillArray: [] });

  const API_URL = `${API_WEB_URLS.BASE || "/api/V1/"}ContractEditDataAppParty/0/token`;

  const loadData = async () => {
    setLoading(true);
    try {
      const vformData = new FormData();
      vformData.append("FromDate", fromDate.toISOString().split("T")[0]);
      vformData.append("ToDate", toDate.toISOString().split("T")[0]);
      vformData.append("ContractType", filterType === "buy" ? "1" : filterType === "sell" ? "2" : "0");
      vformData.append("LiftingStatus", "0"); // Pending

      const res = await Fn_GetReport(dispatch, setState, "FillArray", API_URL, { arguList: { id: 0, formData: vformData } }, true);
      if (res && Array.isArray(res)) {
        setData(res);
      } else if (state.FillArray) {
        setData(state.FillArray);
      }
    } catch (e) {
      console.error("Error loading Pending Lifting:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filterType]);

  const sendWhatsAppReminder = (row) => {
    const text = encodeURIComponent(
      `*REMINDER: PENDING LIFTING/DISPATCH*\nContract No: ${row.ContractNo}\nItem: ${row.ItemName || ''}\nTotal Qty: ${row.Qty} Qtl\nLifted: ${row.TotalLifted || 0} Qtl\n*Pending Balance: ${row.PendingQty || row.Qty} Qtl*\nLifting Due Date: ${row.LiftedToDate ? row.LiftedToDate.split("T")[0] : 'Immediate'}\nPlease arrange lifting/truck dispatch at the earliest.`
    );
    window.open(`https://api.whatsapp.com/send?text=${text}`, "_blank");
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PendingLifting");
    XLSX.writeFile(wb, `Pending_Lifting_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  return (
    <div className="page-content" style={{ padding: "15px" }}>
      <Container fluid>
        <Row className="mb-2 align-items-center">
          <Col xs={12} md={6}>
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center">
              <Truck className="me-2 text-warning" size={22} />
              Pending Lifting & Delivery Order (DO) Tracker (माल उठान / डिस्पैच ट्रैकर)
            </h5>
            <small className="text-muted">Monitor contracts expiring soon to avoid warehouse storage penalties</small>
          </Col>
          <Col xs={12} md={6} className="text-md-end mt-2 mt-md-0 d-flex justify-content-md-end gap-2 align-items-center">
            <Form.Select size="sm" value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width: "130px" }}>
              <option value="all">All Deals</option>
              <option value="buy">🟢 Buy Lifting</option>
              <option value="sell">🔵 Sell Dispatch</option>
            </Form.Select>
            <Button size="sm" variant="primary" onClick={loadData} disabled={loading}>
              <RefreshCw size={14} className={`me-1 ${loading ? "spin" : ""}`} /> Refresh
            </Button>
            <Button size="sm" variant="success" onClick={exportToExcel} disabled={data.length === 0}>
              <Download size={14} className="me-1" /> Excel
            </Button>
          </Col>
        </Row>

        <Card className="shadow-sm border-0">
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="warning" />
                <p className="mt-2 text-muted">Checking lifting schedules and pending quantities...</p>
              </div>
            ) : (
              <Table responsive hover striped bordered className="mb-0 text-center align-middle" style={{ fontSize: "0.85rem" }}>
                <thead className="table-light text-dark">
                  <tr>
                    <th>Type</th>
                    <th>Cont No</th>
                    <th>Date</th>
                    <th>Party / Mill</th>
                    <th>Broker</th>
                    <th>Item</th>
                    <th>Contract Qty</th>
                    <th>Lifted Qty</th>
                    <th className="table-warning text-dark fw-bold">Pending Qty</th>
                    <th>Lifting Due Date</th>
                    <th>Urgency Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="py-4 text-muted">
                        No pending liftings found. All contracts are up-to-date!
                      </td>
                    </tr>
                  ) : (
                    data.map((row, idx) => {
                      const isBuy = row.ContractType === 1 || row.ContractTypeName === "BUY";
                      const pendingQty = parseFloat(row.PendingQty) || (parseFloat(row.Qty) - parseFloat(row.TotalLifted || 0));
                      const dueDate = row.LiftedToDate ? new Date(row.LiftedToDate) : null;
                      const today = new Date();
                      const daysLeft = dueDate ? Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24)) : null;

                      let urgency = <Badge bg="success">🟢 Safe</Badge>;
                      if (daysLeft !== null && daysLeft <= 0) {
                        urgency = <Badge bg="danger">🔴 OVERDUE ({Math.abs(daysLeft)}d)</Badge>;
                      } else if (daysLeft !== null && daysLeft <= 3) {
                        urgency = <Badge bg="warning" text="dark">🟡 DUE SOON ({daysLeft}d)</Badge>;
                      }

                      return (
                        <tr key={idx}>
                          <td>
                            <Badge bg={isBuy ? "success" : "primary"}>
                              {isBuy ? "BUY (खरीद)" : "SELL (बिक्री)"}
                            </Badge>
                          </td>
                          <td className="fw-bold">{row.ContractNo}</td>
                          <td>{row.Date ? row.Date.split("T")[0] : ""}</td>
                          <td className="text-start ps-2 fw-semibold">{row.PartyName}</td>
                          <td>{row.BrokerName || "DIRECT"}</td>
                          <td>{row.ItemName}</td>
                          <td>{parseFloat(row.Qty || 0).toFixed(2)}</td>
                          <td className="text-muted">{parseFloat(row.TotalLifted || 0).toFixed(2)}</td>
                          <td className="table-warning fw-bold text-danger">{(pendingQty || 0).toFixed(2)}</td>
                          <td>{row.LiftedToDate ? row.LiftedToDate.split("T")[0] : "As per DO"}</td>
                          <td>{urgency}</td>
                          <td>
                            <Button size="sm" variant="outline-success" className="py-0 px-2" onClick={() => sendWhatsAppReminder(row)} title="Send WhatsApp Reminder">
                              <MessageCircle size={14} className="me-1" /> WhatsApp
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default PendingLiftingTracker;
